import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { pcmpProjectPickerQueryOptions } from '../../api/pcmpProjects.api';

interface PcmpProjectMultiSelectProps {
  value: number[];
  onChange: (value: number[]) => void;
  id?: string;
}

/** Any number of projects; none means every project. */
export function PcmpProjectMultiSelect({ value, onChange, id }: PcmpProjectMultiSelectProps) {
  const { data: projects, isLoading } = useQuery(pcmpProjectPickerQueryOptions());

  const toggle = (projectId: number, checked: boolean) =>
    onChange(checked ? [...value, projectId] : value.filter((item) => item !== projectId));

  const label =
    value.length === 0
      ? 'All'
      : value.length === 1
        ? (projects?.find((project) => project.id === value[0])?.name ?? '1 project')
        : `${value.length} projects`;

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={isLoading}
            className="h-10 w-full justify-between font-normal"
          />
        }
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="size-4 shrink-0 opacity-60" aria-hidden />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="flex max-h-72 w-72 flex-col gap-1 overflow-y-auto p-2"
      >
        {value.length > 0 ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange([])}>
            Clear ({value.length})
          </Button>
        ) : null}
        {projects?.map((project) => (
          <label
            key={project.id}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
          >
            <Checkbox
              checked={value.includes(project.id)}
              onCheckedChange={(checked) => toggle(project.id, checked === true)}
            />
            <span className="min-w-0 truncate">{project.name}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}
